/* import __COLOCATED_TEMPLATE__ from './trending-topic-card.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import type { TrendingTopic } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topic-trends/trending-topics-data-resources';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type AppService from 'embercom/services/app-service';
import { VISUALIZATION_TYPES } from 'embercom/components/reporting/custom/chart-builder/filter-bar/chart-type-filter';
import { insightsMetrics } from 'embercom/components/reporting/automation/ai-insights/fin-insights/jobs';
import moment from 'moment-timezone';
import Range from 'embercom/models/reporting/range';
import type { LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { metricConfigs } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topics-explorer/common';
import { volumeMetricConfig } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topic-trends/common';
import PALETTE from '@intercom/pulse/lib/palette';
import { cached } from 'tracked-toolbox';

const TREND_CHART_HEIGHT = 88;
const TREND_CHART_WIDTH = 208;

interface Args {
  trendingTopic: TrendingTopic;
  onShowTrendingTopicsPanel: (topic: TrendingTopic) => void;
  trackAnalyticsEvent: (action: string, object: string, extraContext: Record<string, any>) => void;
  selectedWeekStart?: string;
  exploreTrendingTopic?: TrendingTopic;
}

export default class TrendingTopicCard extends Component<Args> {
  @service declare intl: IntlService;
  @service declare appService: AppService;

  get topicName() {
    return this.args.trendingTopic.topicName;
  }

  get subtopicName() {
    return this.args.trendingTopic.subtopicName;
  }

  get isSelected() {
    return (
      this.args.exploreTrendingTopic !== undefined &&
      this.isSameTopic(this.args.trendingTopic, this.args.exploreTrendingTopic)
    );
  }

  private isSameTopic(current: TrendingTopic, selected: TrendingTopic): boolean {
    return (
      current.topicName === selected.topicName &&
      current.subtopicName === selected.subtopicName &&
      current.weekStart === selected.weekStart
    );
  }

  private get appTimezone() {
    return this.appService.app.timezone;
  }

  private get topicWeekBoundaries() {
    let weekStartMoment = moment.utc(this.args.trendingTopic.weekStart).tz(this.appTimezone, true);
    let weekStart = weekStartMoment.clone().startOf('isoWeek');
    let weekEnd = weekStartMoment.clone().endOf('isoWeek');
    return { weekStart, weekEnd };
  }

  get statsDateRange() {
    let { weekStart, weekEnd } = this.topicWeekBoundaries;
    return Range.createFromParams(weekStart.format(), weekEnd.format(), this.appTimezone);
  }

  get statsFilters(): LogicalFilter {
    let { weekStart, weekEnd } = this.topicWeekBoundaries;
    let startDate = weekStart.clone().subtract(5, 'weeks').startOf('day');
    return {
      type: 'and',
      filters: [
        {
          type: 'category',
          data: {
            attribute: 'conversation_custom_fields#ai_insights_topic',
            property: 'conversation_custom_fields#ai_insights_topic',
            values: [this.topicName],
          },
        },
        {
          type: 'category',
          data: {
            attribute: 'conversation_custom_fields#ai_insights_subtopic',
            property: 'conversation_custom_fields#ai_insights_subtopic',
            values: [this.subtopicName],
          },
        },
        {
          type: 'date_range',
          data: {
            attribute: 'conversation.first_closed_at',
            property: 'first_closed_at',
            values: [startDate.valueOf(), weekEnd.valueOf()],
          },
        },
      ],
    };
  }

  get primaryMetricGraphLabel() {
    let translationFor = (name: string) => {
      return this.intl.t(
        `components.reporting.automation.fin-insights.analyse.topic-trends.graph-labels.${name}`,
      );
    };
    return translationFor(this.args.trendingTopic.primaryMetric);
  }

  @cached
  get metricConfigs() {
    return metricConfigs(this.intl);
  }

  get primaryMetricConfig() {
    let primaryMetric = this.args.trendingTopic.primaryMetric;

    if (primaryMetric === 'v1.new_conversations') {
      return volumeMetricConfig(this.intl);
    }

    return this.metricConfigs[primaryMetric];
  }

  get trendChartDateRange() {
    if (this.args.selectedWeekStart) {
      let { weekStart, weekEnd } = this.topicWeekBoundaries;
      let startDate = weekStart.clone().subtract(5, 'weeks').startOf('day');
      return Range.createFromParams(startDate.format(), weekEnd.format(), this.appTimezone);
    }
    return undefined;
  }

  get trendChartDefinition() {
    let volumeMetric = insightsMetrics(this.intl).volume;

    let chartSeries: Array<{
      metric_id: string;
      filters: LogicalFilter;
      color?: string;
      label: string;
      aggregation?: string;
    }> = [
      {
        metric_id: volumeMetric.metric_id,
        aggregation: volumeMetric.aggregation,
        filters: this.statsFilters,
        color: PALETTE['vis-azure-50'],
        label: this.intl.t('ai-agent.analyze.performance.new-conversations'),
      },
      {
        ...this.primaryMetricConfig.seriesConfig,
        filters: this.statsFilters,
        color: this.primaryMetricConfig.seriesConfig.color,
        label: this.primaryMetricConfig.label,
      },
    ];

    return {
      id: `trend-${this.topicName}-${this.subtopicName}`,
      view_by: 'time',
      visualization_type: VISUALIZATION_TYPES.COLUMN_LINE,
      view_by_time_interval: 'week',
      date_range: this.args.selectedWeekStart ? undefined : { selection: 'past_4_weeks' },
      disable_legend: true,
      visualization_options: {
        hide_axes: true,
        chart_height: TREND_CHART_HEIGHT,
        chart_width: TREND_CHART_WIDTH,
      },
      chart_series: chartSeries,
    };
  }

  get primaryMetricChartDefinition() {
    return {
      id: `primary-metric-${this.topicName}-${this.subtopicName}`,
      view_by: null,
      visualization_type: VISUALIZATION_TYPES.COUNTER,
      view_by_time_interval: 'day',
      visualization_options: {
        show_time_comparison: true,
      },
      chart_series: [{ metric_id: this.args.trendingTopic.primaryMetric }],
    };
  }

  @action
  onShowTrendingTopicsPanel(topic: TrendingTopic) {
    this.args.onShowTrendingTopicsPanel(topic);
  }

  @action
  onCardClick() {
    this.onShowTrendingTopicsPanel(this.args.trendingTopic);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TopicTrends::TrendingTopicCard': typeof TrendingTopicCard;
  }
}
